import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchChangeOrderLog, fetchProject, fetchProjectInbox, fetchProjectPayApps } from '../../api/projects'
import { useAuth } from '../../context/AuthContext'
import { fetchPulseCloseout, fetchPulsePayments, fetchPulseTimeline, type PulseEvent, type PulsePaymentRow } from './pulseApi'
import { collapseEvents } from './pulseCollapse.js'
import { eventsToCsv, pulseCsvFilename } from './pulseCsv.js'
import { defaultSince, openPulseWindow } from './pulseLastView.js'
import { openPayAppsFromList, pulseStatus, pulseTotals, retainageHeldFromApps } from './pulseTotals.js'
import { pulseProjectMoneyFromProject } from './pulseProjectMoney.js'
import { openFromEvent, parsePulseOpen, serializePulseOpen, type PulseOpen } from './pulseDrawer.js'
import { rememberPulseScroll, restorePulseScroll } from './pulseScroll.js'
import { last14DaysIso, stripChangedParam } from './pulseWhatChanged.js'
import { typicalPayLine } from './pulsePaymentTiming.js'
import { normalizePulseTab } from './pulseTabs.js'
import PulseTimeline, { PulseFilters } from './PulseTimeline'
import PulseDrawer from './PulseDrawer.tsx'
import PulseDocPanel from './PulseDocPanel'
import PulseCoPanel from './PulseCoPanel'
import PulsePayAppPanel from './PulsePayAppPanel'
import PulsePaymentsPanel from './PulsePaymentsPanel'
import PulseCloseout from './PulseCloseout'
import PulseTotalsBlock from './PulseTotalsBlock'
import PulseStickyHeader from './PulseStickyHeader'
import PulseWhatChanged from './PulseWhatChanged.tsx'
import CoLogTree from './CoLogTree'
import './pulse.css'

export default function PulseScreen() {
  const { projectId } = useParams<{ projectId: string }>()
  const pid = Number(projectId)
  const [params, setParams] = useSearchParams()
  const { user } = useAuth()
  const tab = normalizePulseTab(params.get('tab'))
  const drawer = parsePulseOpen(params.get('open'))
  const [q, setQ] = useState('')
  const [types, setTypes] = useState('')
  const [order, setOrder] = useState<'desc' | 'asc'>('desc')
  const [showChanged, setShowChanged] = useState(false)
  const [changedSince, setChangedSince] = useState('')
  const windowSinceRef = useRef('')
  const scrollRef = useRef(0)

  useEffect(() => {
    if (params.get('changed') || params.get('tab') === 'timeline') {
      setParams(stripChangedParam(params), { replace: true })
    }
  }, [params, setParams])
  useEffect(() => {
    if (!user?.id || !Number.isFinite(pid)) return
    const windowSince = openPulseWindow(window.localStorage, user.id, pid)
    windowSinceRef.current = windowSince
    setChangedSince(windowSince)
  }, [user?.id, pid])
  useEffect(() => { if (!drawer) restorePulseScroll(window, scrollRef.current) }, [drawer])

  const projectQ = useQuery({ queryKey: ['project', pid], queryFn: () => fetchProject(pid), enabled: Number.isFinite(pid) })
  const appsQ = useQuery({ queryKey: ['project-payapps', pid], queryFn: () => fetchProjectPayApps(pid), enabled: Number.isFinite(pid) })
  const inboxQ = useQuery({ queryKey: ['pulse-inbox', pid], queryFn: () => fetchProjectInbox(pid, { status: 'all' }), enabled: Number.isFinite(pid) && tab === 'closeout' })
  const closeoutQ = useQuery({ queryKey: ['pulse-closeout', pid], queryFn: () => fetchPulseCloseout(pid), enabled: Number.isFinite(pid) && tab === 'closeout' })
  const payQ = useQuery({ queryKey: ['pulse-payments', pid], queryFn: () => fetchPulsePayments(pid), enabled: Number.isFinite(pid) })
  const coLogQ = useQuery({ queryKey: ['change-order-log', pid], queryFn: () => fetchChangeOrderLog(pid), enabled: Number.isFinite(pid) })
  useEffect(() => {
    if (!Number.isFinite(pid) || window.location.hash !== '#co-log-tree') return
    const el = document.getElementById('co-log-tree')
    if (!el) return
    const t = window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120)
    return () => window.clearTimeout(t)
  }, [pid, coLogQ.data])
  const tlQ = useQuery({
    queryKey: ['pulse-timeline', pid, q, types, order],
    queryFn: () => fetchPulseTimeline(pid, { q, types, order }),
    enabled: Number.isFinite(pid),
  })
  const changedQ = useQuery({
    queryKey: ['pulse-changed', pid, changedSince],
    queryFn: () => fetchPulseTimeline(pid, { since: new Date(changedSince).toISOString() }),
    enabled: Number.isFinite(pid) && showChanged && !!changedSince,
  })

  const project = projectQ.data
  const apps = appsQ.data || []
  const held = retainageHeldFromApps(apps)
  const pulseMoney = pulseProjectMoneyFromProject(project, apps)
  const totals = pulseTotals({
    original: project?.original_contract, changeOrders: project?.change_order_total,
    billed: pulseMoney.billed,
    collected: pulseMoney.collected,
    held: pulseMoney.held,
    open: pulseMoney.open,
    unpaidReleases: held.releasedUnpaid,
  })
  const status = pulseStatus({ totals, openPayApps: openPayAppsFromList(apps) })
  const typical = typicalPayLine({
    owner: payQ.data?.typical?.owner,
    n: payQ.data?.typical?.n_paid,
    medianDays: payQ.data?.typical?.median_days,
  })
  const events = useMemo(() => collapseEvents(tlQ.data?.events || []) as PulseEvent[], [tlQ.data])
  const changedEvents = useMemo(() => collapseEvents(changedQ.data?.events || []) as PulseEvent[], [changedQ.data])

  const setOpen = useCallback((next: PulseOpen | null, replace = false) => {
    if (next && !drawer) scrollRef.current = rememberPulseScroll()
    const n = new URLSearchParams(params)
    if (next) n.set('open', serializePulseOpen(next))
    else n.delete('open')
    setParams(n, { replace })
    if (!next) restorePulseScroll(window, scrollRef.current)
  }, [params, setParams, drawer])

  function setTab(next: 'pulse' | 'closeout') {
    const n = stripChangedParam(params)
    n.set('tab', next)
    setParams(n, { replace: true })
  }

  function exportCsv() {
    const csv = eventsToCsv(showChanged ? changedEvents : events, {
      coEntries: coLogQ.data?.entries,
      payApps: apps,
      payments: payQ.data,
      project,
    })
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = pulseCsvFilename({ projectNumber: project?.number, view: showChanged ? 'what-changed' : tab })
    a.click()
    URL.revokeObjectURL(a.href)
  }

  function openPayment(row: PulsePaymentRow) {
    setOpen({ kind: 'pay_app', id: row.pay_app_id })
  }

  if (!Number.isFinite(pid)) return null
  if (projectQ.isError) return <div className="pulse-page">Project not found.</div>

  return (
    <div className="pulse-page" data-testid="pulse-page">
      <PulseStickyHeader
        projectId={pid}
        projectNumber={project?.number} projectName={project?.name} owner={project?.owner}
        project={project} payApps={apps}
        tab={tab} onTab={setTab} onWhatChanged={() => setShowChanged(true)}
        onFilesUploaded={() => { void tlQ.refetch(); void inboxQ.refetch(); void closeoutQ.refetch() }}
        onOpenAr={() => setOpen({ kind: 'payments' })}
      />
      {tab === 'closeout' ? (
        <PulseCloseout
          projectId={pid} revised={totals.revised} billed={totals.billed} collected={totals.collected}
          held={held.held} unpaidReleases={held.releasedUnpaid} payApps={apps}
          documents={inboxQ.data || []} handover={closeoutQ.data?.handover || null}
          onOpenWorkBilled={() => { setTab('pulse'); setOpen({ kind: 'payments' }) }}
          onOpenPayments={() => { setTab('pulse'); setOpen({ kind: 'payments', highlight: 'received' }) }}
          onOpenRetainage={() => { setTab('pulse'); setOpen({ kind: 'payments', highlight: 'retainage' }) }}
          onRefresh={() => { void inboxQ.refetch(); void closeoutQ.refetch(); void tlQ.refetch() }}
        />
      ) : (
        <>
          <PulseTotalsBlock
            totals={totals} status={status} typicalLine={typical} onOpen={(next) => setOpen(next)}
            onAskSoren={() => window.dispatchEvent(new CustomEvent('soren:open-ask', { detail: { projectId: pid } }))}
          />
          <PulseFilters
            q={q} types={types} order={order} since="" showSince={false}
            onQ={setQ} onTypes={setTypes} onOrder={setOrder} onSince={() => {}}
            onExport={exportCsv}
            onReset={() => { setQ(''); setTypes(''); setOrder('desc'); setChangedSince(windowSinceRef.current || defaultSince(window.localStorage, user?.id, pid)) }}
          />
          <PulseTimeline
            events={events}
            payments={payQ.data}
            payApps={apps}
            coEntries={coLogQ.data?.entries}
            project={project}
            onOpen={(e: PulseEvent) => { const next = openFromEvent(e); if (next) setOpen(next) }}
            onOpenPayApp={(id) => setOpen({ kind: 'pay_app', id })}
            onReset={() => { setQ(''); setTypes('') }}
          />
          <CoLogTree projectId={pid} onOpenCo={(id) => setOpen({ kind: 'change_order', id })} />
        </>
      )}
      {showChanged && (
        <PulseWhatChanged
          since={changedSince} lastVisit={windowSinceRef.current} events={changedEvents}
          onSince={setChangedSince} onClose={() => setShowChanged(false)}
          onShow14={() => setChangedSince(last14DaysIso())}
          onOpen={(e) => { const next = openFromEvent(e); if (next) setOpen(next) }}
        />
      )}
      <PulseDrawer open={drawer} onClose={() => setOpen(null)}>
        {drawer?.kind === 'document' && <PulseDocPanel projectId={pid} open={drawer} />}
        {drawer?.kind === 'change_order' && <PulseCoPanel projectId={pid} coId={drawer.id} />}
        {drawer?.kind === 'pay_app' && (
          <PulsePayAppPanel projectId={pid} payAppId={drawer.id} payments={payQ.data} onClose={() => setOpen(null)} onOpenPayment={openPayment} />
        )}
        {drawer?.kind === 'payments' && (
          <PulsePaymentsPanel apps={apps} payments={payQ.data} highlight={drawer.highlight} onOpenPayApp={(id) => setOpen({ kind: 'pay_app', id }, true)} onOpenPayment={openPayment} />
        )}
      </PulseDrawer>
    </div>
  )
}
